import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminTwoFactorAuthPage = () => {
  const { post, pass } = useParams();
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [method, setMethod] = useState('email'); // 'email' or 'sms'

  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`admin-code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    if (error) setError('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`admin-code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    const code = verificationCode.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate verification (accept 123456 as valid for demo)
      if (code === '123456') {
        alert('Admin two-factor authentication successful!');
        navigate('/admin');
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert(`Admin verification code sent via ${method === 'email' ? 'email' : 'SMS'}!`);
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const switchMethod = () => {
    setMethod(method === 'email' ? 'sms' : 'email');
    setVerificationCode(['', '', '', '', '', '']);
    setError('');
  };

  const shieldIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
  );

  return (
    <AuthLayout
      title="Admin Two-Factor Authentication"
      subtitle={method === 'email' 
        ? 'Enter the 6-digit code sent to your admin email address'
        : 'Enter the 6-digit code sent to your registered mobile phone'
      }
      icon={shieldIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
      showDemo={true}
      demoText="Use code 123456 to verify"
    >
      <form onSubmit={handleVerification} className="space-y-6">
        {/* Admin Security Notice */}
        <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-red-900 mb-1">Enhanced Security Required</h4>
              <p className="text-sm text-jet-red-800">
                Admin access requires two-factor authentication for security compliance.
              </p>
            </div>
          </div>
        </div>

        {/* Verification Code Input */}
        <div>
          <label className="block text-sm font-medium text-jet-gray-700 mb-3 text-center">
            Admin Verification Code
          </label>
          <div className="flex justify-center space-x-3 mb-4">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`admin-code-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-colors ${
                  error 
                    ? 'border-jet-red-400 focus:border-jet-red-500' 
                    : 'border-jet-gray-300 focus:border-jet-red-400'
                }`}
              />
            ))}
          </div>
          
          {error && (
            <p className="text-sm text-jet-red-600 text-center mb-4">{error}</p>
          )}
        </div>

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          disabled={verificationCode.join('').length !== 6}
          loadingText="Verifying Admin Access..."
          variant="primary"
          className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
        >
          Verify Admin Access
        </LoadingButton>

        {/* Resend Code */}
        <div className="text-center space-y-3">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading || resendCooldown > 0}
            className={`text-sm transition-colors ${
              resendLoading || resendCooldown > 0
                ? 'text-jet-gray-400 cursor-not-allowed'
                : 'text-jet-red-600 hover:text-jet-red-700'
            }`}
          >
            {resendLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jet-gray-400 mr-2"></div>
                Sending...
              </div>
            ) : resendCooldown > 0 ? (
              `Resend admin code in ${resendCooldown}s`
            ) : (
              `Resend admin code via ${method === 'email' ? 'email' : 'SMS'}`
            )}
          </button>

          {/* Switch Method */}
          <div className="text-sm text-jet-gray-600">
            Didn't receive the admin code?{' '}
            <button
              type="button"
              onClick={switchMethod}
              className="text-jet-red-600 hover:text-jet-red-700 font-medium"
            >
              Try {method === 'email' ? 'SMS' : 'email'} instead
            </button>
          </div>
        </div>

        {/* Security Information */}
        <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-blue-900 mb-1">Security Notice</h4>
              <p className="text-sm text-jet-blue-800">
                This verification code is valid for 10 minutes and can only be used once. 
                All admin login attempts are logged for security monitoring.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/admin/auth/login')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            Back to Admin Login
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminTwoFactorAuthPage;